import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CONTINUES_SELF, newBlockId, numberedIndex, parseBlocks, serializeBlocks,
} from '../../../../lib/richEditor/blocks';
import {
  diffText, normalizeMarks, rebaseMarks, sliceMarks, toggleMark, removeMarkRange,
} from '../../../../lib/richEditor/marks';
import { blockClassFor } from '../../../../lib/richEditor/richText';
import RichLine from './RichLine';
import RichToolbar from './RichToolbar';

function placeholderFor(type, index) {
  if (index === 0 && type === 'p') return 'Type here…';
  if (type === 'h1') return 'Heading 1';
  if (type === 'h2') return 'Heading 2';
  if (type === 'h3') return 'Heading 3';
  if (type === 'h4') return 'Heading 4';
  if (type === 'quote') return 'Quote';
  if (type === 'bullet' || type === 'numbered') return 'List';
  return '';
}

/**
 * Notion-like block editor for one section's markdown `content` string.
 * Ports teacher-mobile's RichTextEditor.tsx state machine (block split on
 * Enter, merge/demote on Backspace-at-start, mark toggling) — that logic
 * has no React Native dependency, so it carries over almost verbatim. Only
 * the DOM specifics differ:
 *  - A native <input> never receives a literal "\n" in its change value
 *    (unlike RN's multiline TextInput), so block-splitting is driven by an
 *    explicit Enter keydown at the current caret, not by scanning the
 *    changed text for embedded newlines.
 *  - Multi-line paste into a single-line <input> is intercepted separately
 *    (handlePaste) so pasting an AI-generated multi-paragraph answer still
 *    creates one block per line, matching the Enter-key behavior.
 */
export default function RichTextEditor({ value, onChange }) {
  const [blocks, setBlocks] = useState(() => parseBlocks(value, { legacyDollarToggle: false }));
  const [focusedId, setFocusedId] = useState(null);

  const inputRefs = useRef({});
  const selRefs = useRef({});
  const pendingFocus = useRef(null);
  const pendingSelection = useRef(null);
  const mounted = useRef(false);
  const lastEmitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Our own edits (below) update `lastEmitted` before the parent re-renders
  // with the new `value`, so this effect only fires for changes the parent
  // made itself — e.g. FrameworkSection.jsx appending a `[ref:N]` marker or
  // a symbol at the end of `editContent` from OUTSIDE this component.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setBlocks(parseBlocks(value, { legacyDollarToggle: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    const serialized = serializeBlocks(blocks, { legacyDollarToggle: false });
    lastEmitted.current = serialized;
    onChangeRef.current(serialized);
  }, [blocks]);

  useEffect(() => {
    if (!pendingFocus.current) return;
    const id = pendingFocus.current;
    pendingFocus.current = null;
    const sel = pendingSelection.current;
    pendingSelection.current = null;
    const node = inputRefs.current[id];
    if (!node) return;
    node.focus();
    if (sel) node.setSelectionRange(sel.start, sel.end);
  }, [blocks]);

  const handleChangeText = useCallback((id, newText) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const block = prev[idx];
      const { at, removed, inserted } = diffText(block.text, newText);
      const rebased = rebaseMarks(block.marks, at, removed, inserted);
      const next = [...prev];
      next[idx] = { ...block, text: newText, marks: rebased };
      return next;
    });
  }, []);

  const handleEnterAtCaret = useCallback((id, sel) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const block = prev[idx];
      const caret = Math.max(sel.start, sel.end);

      const isContinuable = CONTINUES_SELF.includes(block.type);
      if (isContinuable && block.text === '') {
        // Enter on an empty list/quote line exits it back to plain text —
        // same block, matching Notion's "empty list item + Enter" escape.
        const next = [...prev];
        next[idx] = { ...block, type: 'p' };
        pendingFocus.current = id;
        pendingSelection.current = { start: 0, end: 0 };
        return next;
      }

      const head = block.text.slice(0, caret);
      const tail = block.text.slice(caret);
      const headMarks = sliceMarks(block.marks, 0, head.length);
      const tailMarks = sliceMarks(block.marks, caret, block.text.length);
      const continuationType = isContinuable ? block.type : 'p';
      const newBlock = { id: newBlockId(), type: continuationType, text: tail, marks: tailMarks };

      const next = [...prev];
      next.splice(idx, 1, { ...block, text: head, marks: headMarks }, newBlock);
      pendingFocus.current = newBlock.id;
      pendingSelection.current = { start: 0, end: 0 };
      return next;
    });
  }, []);

  const handleBackspaceAtStart = useCallback((id) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const block = prev[idx];
      if (block.type !== 'p') {
        const next = [...prev];
        next[idx] = { ...block, type: 'p' };
        return next;
      }
      if (idx === 0) return prev;
      const prevBlock = prev[idx - 1];
      const caret = prevBlock.text.length;
      const mergedMarks = normalizeMarks([
        ...prevBlock.marks,
        ...block.marks.map((m) => ({ ...m, start: m.start + caret, end: m.end + caret })),
      ]);
      const next = [...prev];
      next.splice(idx - 1, 2, { ...prevBlock, text: prevBlock.text + block.text, marks: mergedMarks });
      pendingFocus.current = prevBlock.id;
      pendingSelection.current = { start: caret, end: caret };
      return next;
    });
  }, []);

  const handlePaste = useCallback((id, e) => {
    const pasted = e.clipboardData.getData('text/plain');
    if (!pasted || !pasted.includes('\n')) return; // let the browser handle single-line paste normally
    e.preventDefault();
    const lines = pasted.split('\n');
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const block = prev[idx];
      const sel = selRefs.current[id] ?? { start: block.text.length, end: block.text.length };
      const start = Math.min(sel.start, sel.end);
      const end = Math.max(sel.start, sel.end);
      const before = block.text.slice(0, start);
      const after = block.text.slice(end);
      const updatedFirst = { ...block, text: before + lines[0], marks: sliceMarks(block.marks, 0, start) };
      const middleBlocks = lines.slice(1, -1).map((line) => ({ id: newBlockId(), type: 'p', text: line, marks: [] }));
      const lastLine = lines[lines.length - 1];
      const lastBlock = { id: newBlockId(), type: 'p', text: lastLine + after, marks: [] };
      const next = [...prev];
      next.splice(idx, 1, updatedFirst, ...middleBlocks, lastBlock);
      pendingFocus.current = lastBlock.id;
      pendingSelection.current = { start: lastLine.length, end: lastLine.length };
      return next;
    });
  }, []);

  const setBlockType = useCallback((id, type) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx], type };
      pendingFocus.current = id;
      return next;
    });
  }, []);

  const applyInline = useCallback((id, kind, param) => {
    const sel = selRefs.current[id];
    if (!sel || sel.start === sel.end) return;
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const block = prev[idx];
      const start = Math.min(sel.start, sel.end);
      const end = Math.max(sel.start, sel.end);
      // For color/highlight, `param === null` means "clear" (the toolbar's
      // reset swatch) — strip every mark of that kind in range rather than
      // toggling a colorless mark on, matching teacher-mobile's applyColor.
      const marks = (kind === 'color' || kind === 'bg') && param === null
        ? removeMarkRange(block.marks, kind, start, end)
        : toggleMark(block.marks, kind, start, end, param);
      const next = [...prev];
      next[idx] = { ...block, marks };
      return next;
    });
  }, []);

  const focusedBlock = blocks.find((b) => b.id === focusedId) ?? null;

  return (
    <div className="w-full">
      <RichToolbar
        activeBlockType={focusedBlock ? focusedBlock.type : null}
        disabled={!focusedId}
        onSetBlockType={(type) => focusedId && setBlockType(focusedId, type)}
        onApplyInline={(kind, param) => focusedId && applyInline(focusedId, kind, param)}
      />
      <div className="mt-2 space-y-0.5">
        {blocks.map((block, index) => {
          const isBullet = block.type === 'bullet';
          const isNumbered = block.type === 'numbered';
          const isQuote = block.type === 'quote';
          return (
            <div key={block.id} className={`flex items-start ${isQuote ? 'border-l-2 border-gray-300 pl-2.5' : ''}`}>
              {isBullet && (
                <span className="w-5 pt-2.5 flex justify-center flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                </span>
              )}
              {isNumbered && (
                <span className="min-w-5 pt-0.5 text-right mr-0.5 flex-shrink-0 text-gray-500 text-[15px]">
                  {numberedIndex(blocks, index)}.
                </span>
              )}
              <RichLine
                inputRef={(node) => { inputRefs.current[block.id] = node; }}
                text={block.text}
                marks={block.marks}
                blockClassName={blockClassFor(block.type)}
                placeholder={placeholderFor(block.type, index)}
                onChangeText={(t) => handleChangeText(block.id, t)}
                onSelectionChange={(sel) => { selRefs.current[block.id] = sel; }}
                onFocus={() => setFocusedId(block.id)}
                onBlur={() => setFocusedId((cur) => (cur === block.id ? null : cur))}
                onKeyDown={(e) => {
                  // Read the live DOM selection here rather than the cached
                  // selRefs value: setSelectionRange() only fires the
                  // 'select' event when the selection actually changes, so
                  // a caret already sitting at the target position (e.g.
                  // right after focus) never updates the cache — reading
                  // e.target directly is always accurate at keydown time.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleEnterAtCaret(block.id, { start: e.target.selectionStart, end: e.target.selectionEnd });
                    return;
                  }
                  if (e.key === 'Backspace') {
                    if (e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
                      e.preventDefault();
                      handleBackspaceAtStart(block.id);
                    }
                  }
                }}
                onPaste={(e) => handlePaste(block.id, e)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
